import { MouseEventHandler, ReactNode } from 'react';
import styled, { CSSProperties } from 'styled-components';

interface IProps {
  // 하위 엘리먼트
  children: ReactNode;

  // 색상
  color?: 'wakeup' | 'main' | string;

  // 크기 (default = 60px)
  size?: number;

  // 위치 (default: bottom: 30px, right: 30px)
  bottom?: number;
  right?: number;
  top?: number;
  left?: number;

  // z-index (default = 1000)
  zIndex?: number;

  // 클릭 이벤트
  onClick?: MouseEventHandler;

  // 링크 URL (href가 있으면 <a> 태그로 렌더링)
  href?: string;
  target?: string;
  rel?: string;

  // 접근성
  ariaLabel?: string;

  style?: CSSProperties;
}

const FloatingButtonTag = styled.button<IProps>`
  position: fixed;
  bottom: ${({ bottom }) => (bottom !== undefined ? `${bottom}px` : '30px')};
  right: ${({ right }) => (right !== undefined ? `${right}px` : '30px')};
  ${({ top }) => (top !== undefined ? `top: ${top}px;` : '')}
  ${({ left }) => (left !== undefined ? `left: ${left}px;` : '')}
  
  width: ${({ size }) => (size ? `${size}px` : '60px')};
  height: ${({ size }) => (size ? `${size}px` : '60px')};
  border-radius: 50%;
  
  background-color: ${({ theme, color }) => {
    if (color === 'main') return theme.pltoColor;
    if (color === 'wakeup') return theme.wakeUpColor;
    return color || theme.pltoColor;
  }};
  
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${({ size }) => (size ? `${size * 0.47}px` : '28px')};
  font-weight: 600;
  text-decoration: none;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  z-index: ${({ zIndex }) => (zIndex !== undefined ? zIndex : 1000)};
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FloatingLinkTag = styled.a<IProps>`
  position: fixed;
  bottom: ${({ bottom }) => (bottom !== undefined ? `${bottom}px` : '30px')};
  right: ${({ right }) => (right !== undefined ? `${right}px` : '30px')};
  ${({ top }) => (top !== undefined ? `top: ${top}px;` : '')}
  ${({ left }) => (left !== undefined ? `left: ${left}px;` : '')}
  
  width: ${({ size }) => (size ? `${size}px` : '60px')};
  height: ${({ size }) => (size ? `${size}px` : '60px')};
  border-radius: 50%;
  
  background-color: ${({ theme, color }) => {
    if (color === 'main') return theme.pltoColor;
    if (color === 'wakeup') return theme.wakeUpColor;
    return color || theme.pltoColor;
  }};
  
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${({ size }) => (size ? `${size * 0.47}px` : '28px')};
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  z-index: ${({ zIndex }) => (zIndex !== undefined ? zIndex : 1000)};
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0);
  }
`;

function FloatingButton(props: IProps) {
  const { href, target, rel, ariaLabel, children, ...restProps } = props;

  // href가 있으면 <a> 태그로 렌더링
  if (href) {
    return (
      <FloatingLinkTag href={href} target={target} rel={rel} aria-label={ariaLabel} {...restProps}>
        {children}
      </FloatingLinkTag>
    );
  }

  return (
    <FloatingButtonTag onClick={props.onClick} aria-label={ariaLabel} {...restProps}>
      {children}
    </FloatingButtonTag>
  );
}

export default FloatingButton;

